/* eslint-disable camelcase -- external_redirect_allowlist is the Config Hub contract key (snake_case). */
// returnUrl validator (SIM-3 / ACS-4127): shared open-redirect security control for the
// redirect-based sign-in flow. Accepts same-origin URLs and Config-Hub-allowlisted external
// hosts; otherwise returns the locale-correct homepage. Pure, isomorphic, no navigation.
import LanguageLocale from 'utils/LanguageLocale';

const { LANGUAGE_CODES } = LanguageLocale;

// Rejected pre-parse so a protocol-relative `//host` can never reach new URL() and be
// mistaken for same-origin. Matched case-insensitively against the trimmed input.
// eslint-disable-next-line no-script-url -- 'javascript:'/'data:' are security-vector prefixes to reject, not executable URLs.
const DANGEROUS_PREFIXES = ['javascript:', 'data:', '//'];

/**
 * Resolve the locale-correct homepage path used as the safe fallback destination.
 * When `locale` is blank it is derived from the current session via LanguageLocale
 * so a Canada-FR user is never dropped on the US homepage.
 * @returns {string} Homepage path: '/ca/fr/' (fr_CA), '/ca/en/' (en_CA), or '/' (default).
 */
const localeHomepage = locale => {
    let resolved = locale;

    if (!resolved) {
        try {
            resolved = LanguageLocale.getCurrentLanguageCountryCode();
        } catch (e) {
            // SSR/early-boot: locale globals (cookies/Sephora) may be absent and
            // getCurrentLanguageCountryCode can throw. Fail closed to the root path
            // rather than let the exception defeat the validator's safe-fallback contract.
            return '/';
        }
    }

    if (resolved === LANGUAGE_CODES.frenchCA) {
        return '/ca/fr/';
    }

    if (resolved === LANGUAGE_CODES.englishCA) {
        return '/ca/en/';
    }

    return '/';
};

/**
 * Validate a returnUrl for open-redirect safety.
 * Accepts same-origin URLs (absolute or relative) and external hosts present in the
 * Config Hub allowlist (`Sephora.configurationSettings.external_redirect_allowlist`,
 * fail-closed to [] when missing). Rejects javascript:/data:/protocol-relative schemes,
 * malformed URLs, and non-allowlisted external hosts, returning the locale homepage.
 * Pure function: performs no navigation side-effect.
 * @param {?string} [returnUrl] - The candidate URL from the returnUrl query param (may be null/undefined).
 * @param {string} [currentLocale] - Locale for the homepage fallback.
 * @returns {string} A safe URL to navigate to (the input when valid, else the homepage).
 */
const validateReturnUrl = (returnUrl, currentLocale) => {
    if (!returnUrl) {
        return localeHomepage(currentLocale);
    }

    const trimmed = returnUrl.trim();
    const lower = trimmed.toLowerCase();

    if (DANGEROUS_PREFIXES.some(prefix => lower.startsWith(prefix))) {
        return localeHomepage(currentLocale);
    }

    // SSR/non-browser: no window means we cannot establish same-origin, so fail closed to the
    // homepage rather than throw a ReferenceError. In practice the hook only calls this inside a
    // browser effect, so this guard never fires client-side (and the `typeof window` SSR branch is
    // unreachable under jsdom, which always defines window — hence the coverage ignore).
    /* istanbul ignore next -- SSR-only guard: jsdom always defines window, so the false side is unreachable in tests. */
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : null;

    /* istanbul ignore if -- unreachable under jsdom (origin is always set); defensive SSR fail-closed. */
    if (!origin) {
        return localeHomepage(currentLocale);
    }

    let parsed;

    try {
        parsed = new URL(trimmed, origin);
    } catch (e) {
        return localeHomepage(currentLocale);
    }

    // Reject URLs carrying embedded credentials (https://user:pass@host/...). These obscure the true
    // destination and are an open-redirect/phishing vector even for an otherwise same-origin or
    // allowlisted host, so this check precedes BOTH accept paths below.
    if (parsed.username || parsed.password) {
        return localeHomepage(currentLocale);
    }

    if (parsed.origin === origin) {
        return trimmed;
    }

    // Fail-closed when the Sephora global or its config is absent (SSR / early boot). The
    // `typeof Sephora` SSR side is unreachable under jsdom (Sephora is always defined in tests);
    // the config-absent path IS covered (V-17b).
    /* istanbul ignore next -- `typeof Sephora === 'undefined'` is an SSR-only side, unreachable under jsdom. */
    const cfg = (typeof Sephora !== 'undefined' && Sephora.configurationSettings) || {};
    // Coerce a non-array config value to [] before mapping — a malformed Config Hub value (e.g. a
    // string) would otherwise make .map() throw and defeat the fail-closed behavior.
    const rawAllowlist = Array.isArray(cfg.external_redirect_allowlist) ? cfg.external_redirect_allowlist : [];
    // Normalize allowlist entries (trim + lowercase) so incidental casing/whitespace in the
    // Config Hub value can't cause a false-negative against a legitimate partner host.
    // `parsed.hostname` is already lowercased by the URL API. Still an EXACT match (not substring),
    // so lookalike/subdomain hosts stay rejected (V-11).
    const allowlist = rawAllowlist.map(host => String(host).trim().toLowerCase());

    if (allowlist.includes(parsed.hostname)) {
        return trimmed;
    }

    return localeHomepage(currentLocale);
};

export {
    validateReturnUrl, localeHomepage
};
